import {
  keywordAlphabet,
  lettersOnly,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";

/**
 * The Playfair cipher: a 5 × 5 key square, and the message enciphered two letters at a
 * time.
 *
 * Twenty-five cells for twenty-six letters means one letter has to go. The usual answer
 * is to merge I and J (J is written as I); the other is to drop Q from the square and
 * from the message. Both are offered — they produce different squares and different
 * cipher text, so a reader matching a textbook needs the one the textbook used.
 *
 * Preparing the message ("digraphs"): letters only, in capitals, split into pairs; a pair
 * of identical letters is split by a filler (BALLOON → BA LX LO ON) and an odd last
 * letter is padded with one. When the letter being split or padded is the filler itself,
 * a second filler stands in, because XX would be a doubled pair again.
 */

export type PlayfairSquareKind = "merge-ij" | "drop-q";
export type PlayfairFiller = "X" | "Z" | "Q";
export type PlayfairRule = "row" | "column" | "rectangle";

const SIZE = 5;
const MERGE_IJ_ALPHABET = "ABCDEFGHIKLMNOPQRSTUVWXYZ";
const DROP_Q_ALPHABET = "ABCDEFGHIJKLMNOPRSTUVWXYZ";
const FILLER_PREFERENCE = ["X", "Q", "Z"] as const;

function squareAlphabet(kind: PlayfairSquareKind): string {
  return kind === "merge-ij" ? MERGE_IJ_ALPHABET : DROP_Q_ALPHABET;
}

/** A capital letter as the square knows it: J → I when merged; "" for Q when dropped. */
export function playfairLetter(
  letter: string,
  kind: PlayfairSquareKind
): string {
  if (kind === "merge-ij") {
    return letter === "J" ? "I" : letter;
  }
  return letter === "Q" ? "" : letter;
}

/** The 25 letters of the key square, row by row. */
export function playfairSquare(
  keyword: string,
  kind: PlayfairSquareKind
): string {
  return keywordAlphabet(keyword, squareAlphabet(kind), (letter) =>
    playfairLetter(letter, kind)
  );
}

/**
 * The filler, and the stand-in used when the letter being split or padded is the filler
 * itself: X → Q (or Z when Q is dropped), Z → X, Q → X. A filler the square lacks (Q in
 * a square without Q) falls back to X.
 */
export function playfairFillers(
  filler: PlayfairFiller,
  kind: PlayfairSquareKind
): readonly [string, string] {
  const alphabet = squareAlphabet(kind);
  const primary = alphabet.includes(filler) ? filler : "X";
  const alternate =
    FILLER_PREFERENCE.find(
      (letter) => letter !== primary && alphabet.includes(letter)
    ) ?? "Z";
  return [primary, alternate];
}

/** The message's letters as the square knows them, and how many Qs had to be dropped. */
export function playfairLetters(
  text: string,
  kind: PlayfairSquareKind
): { letters: string; droppedQ: number } {
  let letters = "";
  let droppedQ = 0;
  for (const letter of lettersOnly(text)) {
    const mapped = playfairLetter(letter, kind);
    if (mapped === "") {
      droppedQ += 1;
    } else {
      letters += mapped;
    }
  }
  return { letters, droppedQ };
}

export interface PlayfairDigraph {
  pair: string;
  /** True when the second letter is a filler the preparation added. */
  filled: boolean;
}

/** Split prepared letters into pairs, separating doubles and padding an odd end. */
export function playfairDigraphs(
  letters: string,
  fillers: readonly [string, string]
): PlayfairDigraph[] {
  const [filler, alternate] = fillers;
  const digraphs: PlayfairDigraph[] = [];
  let index = 0;
  while (index < letters.length) {
    const first = letters.charAt(index);
    const second = letters.charAt(index + 1);
    if (second === "" || second === first) {
      const pad = first === filler ? alternate : filler;
      digraphs.push({ pair: `${first}${pad}`, filled: true });
      index += 1;
    } else {
      digraphs.push({ pair: `${first}${second}`, filled: false });
      index += 2;
    }
  }
  return digraphs;
}

/** Row and column (0-based) of a letter in the square. */
export function squarePosition(
  square: string,
  letter: string
): { row: number; column: number } {
  const index = square.indexOf(letter);
  return { row: Math.floor(index / SIZE), column: index % SIZE };
}

function cell(square: string, row: number, column: number): string {
  return square.charAt(mod(row, SIZE) * SIZE + mod(column, SIZE));
}

/** Encipher (direction 1) or decipher (direction −1) one pair. */
export function playfairPair(
  square: string,
  pair: string,
  direction: 1 | -1
): { output: string; rule: PlayfairRule } {
  const a = squarePosition(square, pair.charAt(0));
  const b = squarePosition(square, pair.charAt(1));
  if (a.row === b.row) {
    return {
      output: `${cell(square, a.row, a.column + direction)}${cell(square, b.row, b.column + direction)}`,
      rule: "row",
    };
  }
  if (a.column === b.column) {
    return {
      output: `${cell(square, a.row + direction, a.column)}${cell(square, b.row + direction, b.column)}`,
      rule: "column",
    };
  }
  return {
    output: `${cell(square, a.row, b.column)}${cell(square, b.row, a.column)}`,
    rule: "rectangle",
  };
}

export interface PlayfairStep {
  input: string;
  output: string;
  rule: PlayfairRule;
  /** Encoding: the second letter of `input` is a filler. */
  filled: boolean;
}

export interface PlayfairOptions {
  keyword: string;
  kind: PlayfairSquareKind;
  filler: PlayfairFiller;
}

export interface PlayfairResult {
  square: string;
  steps: PlayfairStep[];
  /** The result as one run of capitals. */
  output: string;
  droppedQ: number;
}

export function playfairEncode(
  text: string,
  options: PlayfairOptions
): PlayfairResult {
  const square = playfairSquare(options.keyword, options.kind);
  const { letters, droppedQ } = playfairLetters(text, options.kind);
  const fillers = playfairFillers(options.filler, options.kind);
  const steps = playfairDigraphs(letters, fillers).map(({ pair, filled }) => ({
    input: pair,
    filled,
    ...playfairPair(square, pair, 1),
  }));
  return {
    square,
    steps,
    output: steps.map((step) => step.output).join(""),
    droppedQ,
  };
}

export interface PlayfairDecodeResult extends PlayfairResult {
  /** A last letter with no partner — real Playfair cipher text has even length. */
  unpaired: string;
  /** Pairs of one letter twice, which a Playfair encoder never produces. */
  doubled: number;
  /** Indexes into `output` of letters that look like fillers to remove by hand. */
  likelyFillers: number[];
}

/** Fillers sit second in a pair, between two equal letters or at the very end. */
function findLikelyFillers(
  plain: string,
  fillers: readonly [string, string]
): number[] {
  const found: number[] = [];
  for (let index = 1; index < plain.length; index += 2) {
    const letter = plain.charAt(index);
    if (!fillers.includes(letter)) {
      continue;
    }
    const isLast = index === plain.length - 1;
    const splitsDouble = plain.charAt(index - 1) === plain.charAt(index + 1);
    if (isLast || splitsDouble) {
      found.push(index);
    }
  }
  return found;
}

export function playfairDecode(
  text: string,
  options: PlayfairOptions
): PlayfairDecodeResult {
  const square = playfairSquare(options.keyword, options.kind);
  const { letters, droppedQ } = playfairLetters(text, options.kind);
  const fillers = playfairFillers(options.filler, options.kind);
  const steps: PlayfairStep[] = [];
  let doubled = 0;
  for (let index = 0; index + 1 < letters.length; index += 2) {
    const pair = letters.slice(index, index + 2);
    if (pair.charAt(0) === pair.charAt(1)) {
      doubled += 1;
    }
    steps.push({
      input: pair,
      filled: false,
      ...playfairPair(square, pair, -1),
    });
  }
  const output = steps.map((step) => step.output).join("");
  return {
    square,
    steps,
    output,
    droppedQ,
    unpaired: letters.length % 2 === 1 ? (letters.at(-1) ?? "") : "",
    doubled,
    likelyFillers: findLikelyFillers(output, fillers),
  };
}
